import React, { useEffect, useRef } from 'react';
import Layout from '../components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Heart, Clock, Users, Award } from 'lucide-react';

// Timeline data
const timelineEvents = [{
  year: '2022',
  title: 'Fondation de l\'entreprise',
  description: 'Lancement d\'Entretiens McBain, avec pour mission de fournir des services complets d\'entretien extérieur résidentiel alliant qualité, fiabilité et professionnalisme.'
}, {
  year: '2023',
  title: 'Diversification des services',
  description: 'Expansion de notre offre pour inclure l\'entretien paysager des propriétés commerciales, consolidant notre présence sur le marché local.'
}, {
  year: '2024',
  title: 'Modernisation de notre parc d\'équipements',
  description: 'Investissement stratégique dans des équipements à la fine pointe de la technologie afin d\'accroître l\'efficacité, la sécurité et la qualité de nos prestations.'
}, {
  year: '2025',
  title: 'Croissance de l\'équipe et de la clientèle',
  description: 'Recrutement et formation de nouveaux talents pour soutenir l\'augmentation constante de la demande et assurer un service exemplaire à notre clientèle grandissante.'
}];

// Team members data
const teamMembers = [{
  name: 'Michel McBain',
  position: 'Fondateur & Directeur',
  bio: 'Avec plus de 15 ans d\'expérience dans l\'entretien paysager, Michel apporte une vision et une expertise technique à chaque projet.',
  image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&h=400&fit=crop'
}, {
  name: 'Sophie Tremblay',
  position: 'Responsable des opérations',
  bio: 'Experte en coordination d\'équipe et en planification de projets d\'entretien paysager.',
  image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=400&h=400&fit=crop'
}, {
  name: 'David Lavoie',
  position: 'Chef d\'équipe terrain',
  bio: 'Spécialiste en entretien de terrains et en déneigement avec une attention particulière aux détails.',
  image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop'
}, {
  name: 'Isabelle Côté',
  position: 'Responsable service client',
  bio: 'Dévouée à assurer une expérience client exceptionnelle, de la consultation initiale jusqu\'à la fin du projet.',
  image: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=400&h=400&fit=crop'
}];

// Core values
const coreValues = [{
  icon: <Heart size={40} />,
  title: 'Passion',
  description: 'Nous apportons enthousiasme et dévouement à chaque projet, traitant chaque terrain comme unique.'
}, {
  icon: <Clock size={40} />,
  title: 'Fiabilité',
  description: 'Nous respectons les délais, communiquons clairement et livrons ce que nous promettons.'
}, {
  icon: <Users size={40} />,
  title: 'Collaboration',
  description: 'Nous travaillons en étroite collaboration avec nos clients pour comprendre leurs besoins.'
}, {
  icon: <Award size={40} />,
  title: 'Excellence',
  description: 'Nous poursuivons les normes les plus élevées dans nos services et notre professionnalisme.'
}];
const About = () => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const valuesRef = useRef<HTMLDivElement>(null);
  const teamRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          Array.from(entry.target.children).forEach((child, index) => {
            setTimeout(() => {
              (child as HTMLElement).classList.add('revealed');
            }, index * 200);
          });
          observer.unobserve(entry.target);
        }
      });
    }, {
      threshold: 0.1
    });
    [timelineRef.current, valuesRef.current, teamRef.current].forEach(ref => {
      if (ref) observer.observe(ref);
    });
    return () => {
      [timelineRef.current, valuesRef.current, teamRef.current].forEach(ref => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, []);
  return <Layout>
      {/* Hero Section */}
      <div className="relative pt-20 pb-16 md:pb-24 bg-brand-green text-white">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
          backgroundImage: "url('https://images.unsplash.com/photo-1513836279014-a89f7a76ae86')"
        }}></div>
        </div>
        <div className="container-wide relative z-10 text-center pt-16 pb-8">
          <h1 className="section-title mb-6">À Propos</h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Une équipe passionnée de professionnels dédiée à l'entretien paysager exceptionnel
          </p>
        </div>
      </div>
      
      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="container-tight">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="w-full lg:w-1/2">
              <h2 className="section-subtitle mb-6">Notre Histoire</h2>
              <p className="text-gray-700 mb-4">
                Fondée en 2022 par Wilson McBain, Entretiens McBain a débuté avec une mission claire : offrir des services d'entretien extérieur résidentiel alliant qualité, fiabilité et professionnalisme.
              </p>
              <p className="text-gray-700 mb-4">
                Au cours de notre première année, nous nous sommes établis comme un fournisseur de confiance pour l'entretien résidentiel. Dès 2023, nous avons diversifié nos services pour inclure l'entretien paysager des propriétés commerciales, consolidant notre présence sur le marché local.
              </p>
              <p className="text-gray-700 mb-4">
                En 2024, nous avons investi stratégiquement dans des équipements à la fine pointe de la technologie afin d'accroître l'efficacité, la sécurité et la qualité de nos prestations, permettant à notre équipe de travailler avec plus de précision et d'efficacité.
              </p>
              <p className="text-gray-700 mb-6">
                Pour 2025, notre vision est de continuer notre croissance à travers le recrutement et la formation de nouveaux talents pour soutenir la demande croissante et assurer un service exemplaire à notre clientèle grandissante.
              </p>
            </div>
            <div className="w-full lg:w-1/2">
              <img src="/lovable-uploads/e7183bcd-d93c-4173-99ce-bd49b08862b4.png" alt="Entretien de haie par Entretiens McBain" className="rounded-lg shadow-xl w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>
      
      {/* Timeline Section */}
      <section className="py-20 bg-brand-stone-light">
        <div className="container-tight">
          <div className="text-center mb-16">
            <h2 className="section-subtitle">Notre Parcours</h2>
            <p className="text-gray-700 max-w-2xl mx-auto">
              Explorez les moments clés de l'histoire de notre entreprise
            </p>
          </div>
          
          <div ref={timelineRef} className="relative">
            {/* Timeline line */}
            <div className="absolute left-1/2 transform -translate-x-1/2 top-0 bottom-0 w-1 bg-brand-green"></div>
            
            {/* Timeline items */}
            {timelineEvents.map((event, index) => <div key={event.year} className={`reveal-on-scroll flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} mb-12 md:mb-16 items-center`}>
                {/* Year bubble */}
                <div className="flex-shrink-0 z-10">
                  <div className="w-16 h-16 rounded-full bg-brand-green text-white flex items-center justify-center text-xl font-bold shadow-lg">
                    {event.year}
                  </div>
                </div>
                
                {/* Content */}
                <div className={`bg-white rounded-lg shadow-lg p-6 mt-4 md:mt-0 md:w-5/12 ${index % 2 === 0 ? 'md:mr-8' : 'md:ml-8'}`}>
                  <h3 className="text-lg font-bold mb-2 text-brand-green">{event.title}</h3>
                  <p className="text-gray-700">{event.description}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>
      
      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="container-tight">
          <div className="text-center mb-16">
            <h2 className="section-subtitle mb-4">Nos Valeurs</h2>
            <p className="text-gray-700 max-w-2xl mx-auto">
              Ces principes guident tout ce que nous faisons
            </p>
          </div>
          
          <div ref={valuesRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {coreValues.map(value => <div key={value.title} className="reveal-on-scroll bg-brand-stone rounded-lg p-6 text-center transition-transform hover:-translate-y-1">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-brand-green text-white mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-display font-medium mb-3">{value.title}</h3>
                <p className="text-gray-700">{value.description}</p>
              </div>)}
          </div>
        </div>
      </section>
      
      {/* Team Members */}
      
      
      {/* CTA Section */}
      <section className="py-20 bg-brand-green text-white text-center">
        <div className="container-tight">
          <h2 className="section-title mb-6">Prêt à Travailler Avec Nous?</h2>
          <p className="text-xl mb-8 text-gray-200 max-w-3xl mx-auto">
            Contactez-nous pour une consultation ou demandez un devis pour votre projet.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button href="/contact" size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-brand-green">
              Contactez Notre Équipe
            </Button>
            <Button href="/quote" size="lg" className="bg-white text-brand-green hover:bg-brand-stone">
              Demander un Devis
            </Button>
          </div>
        </div>
      </section>
    </Layout>;
};
export default About;